
import React from 'react';
import { BarChart, Zap, Settings, Lightbulb, Target } from "lucide-react";

const KpisTab = () => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium flex items-center">
        <BarChart className="mr-2 h-5 w-5" /> Key Performance Indicators (KPIs)
      </h3>
      <p>
        KPIs measure your performance across different aspects of mining operations.
        Improving these metrics is essential for meeting career objectives and advancing
        your career.
      </p>
      
      <div className="border rounded-md p-4 bg-muted/20">
        <h4 className="font-medium mb-2">Primary KPIs</h4>
        <div className="space-y-3">
          <div className="border border-primary/20 rounded p-3">
            <h5 className="font-medium flex items-center">
              <Zap className="h-4 w-4 mr-2 text-amber-500" />
              Uptime (Target: 99%)
            </h5>
            <p className="text-sm">Measures how consistently your mining operation runs without downtime</p>
            <div className="mt-2 flex items-center text-xs">
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-amber-500 h-2 rounded-full" style={{ width: '90%' }}></div>
              </div>
              <span className="ml-2 text-muted-foreground">90% to target</span>
            </div>
            <p className="text-xs mt-1 text-muted-foreground">Improved by: Technical skills, maintenance decisions, equipment quality</p>
          </div>
          
          <div className="border border-primary/20 rounded p-3">
            <h5 className="font-medium flex items-center">
              <Zap className="h-4 w-4 mr-2 text-blue-500" />
              Energy Cost (Target: $0.05/kWh)
            </h5>
            <p className="text-sm">The cost of electricity for your mining operation - lower is better</p>
            <div className="mt-2 flex items-center text-xs">
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full" style={{ width: '70%' }}></div>
              </div>
              <span className="ml-2 text-muted-foreground">70% to target</span>
            </div>
            <p className="text-xs mt-1 text-muted-foreground">Improved by: Operations skills, renewable energy, location decisions</p>
          </div>
          
          <div className="border border-primary/20 rounded p-3">
            <h5 className="font-medium flex items-center">
              <BarChart className="h-4 w-4 mr-2 text-green-500" />
              Profit Margin (Target: 25%)
            </h5>
            <p className="text-sm">Your operation's profitability as a percentage of revenue</p>
            <div className="mt-2 flex items-center text-xs">
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: '80%' }}></div>
              </div>
              <span className="ml-2 text-muted-foreground">80% to target</span>
            </div>
            <p className="text-xs mt-1 text-muted-foreground">Improved by: Business skills, strategic decisions, market timing</p>
          </div>
          
          <div className="border border-primary/20 rounded p-3">
            <h5 className="font-medium flex items-center">
              <Settings className="h-4 w-4 mr-2 text-purple-500" />
              Efficiency (Target: 85%)
            </h5>
            <p className="text-sm">How effectively you're using your hardware and resources</p>
            <div className="mt-2 flex items-center text-xs">
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-purple-500 h-2 rounded-full" style={{ width: '75%' }}></div>
              </div>
              <span className="ml-2 text-muted-foreground">75% to target</span>
            </div>
            <p className="text-xs mt-1 text-muted-foreground">Improved by: Technical skills, operations decisions, cooling optimization</p>
          </div>
        </div>
      </div>
      
      <div className="border-l-4 border-primary p-4 bg-primary/5">
        <h4 className="font-medium flex items-center">
          <Lightbulb className="mr-2 h-4 w-4 text-primary" /> KPI Improvement
        </h4>
        <p className="text-sm">
          KPIs improve through:
        </p>
        <ul className="list-disc list-inside space-y-1 text-sm mt-2">
          <li>Natural improvements over time (small increases each year)</li>
          <li>Challenge decisions (immediate effects, positive or negative)</li>
          <li>Skill acquisition (permanent boosts to relevant KPIs)</li>
          <li>Career level promotions (one-time boosts when advancing)</li>
        </ul>
      </div>
      
      <div className="p-4 border rounded-md bg-muted/10">
        <h4 className="font-medium flex items-center mb-2">
          <Target className="mr-2 h-4 w-4 text-web3-blue" /> Target Thresholds
        </h4>
        <p className="text-sm mb-3">
          Different career paths prioritize different KPIs:
        </p>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="p-2 border rounded">
            <span className="font-medium">Technical Path</span>: Prioritizes Efficiency & Uptime
          </div>
          <div className="p-2 border rounded">
            <span className="font-medium">Management Path</span>: Focuses on Profit Margin
          </div>
          <div className="p-2 border rounded">
            <span className="font-medium">Entrepreneurial Path</span>: Balances all KPIs
          </div>
          <div className="p-2 border rounded">
            <span className="font-medium">Research Path</span>: Emphasizes Energy Cost & Efficiency
          </div>
        </div>
      </div>
    </div>
  );
};

export default KpisTab;
